import React from 'react';
import type {
  InputNode,
  ReportListResult,
  ReportListItem,
  ShopListFetcher,
} from '../types';
import type { ResolveNameOutcome } from '../hooks/useReportAnalyze';
import { DebugNode } from './DebugNode';
import { TextNode } from './TextNode';
import { TagNode } from './TagNode';
import { VariableNode } from './VariableNode';
import { NumberInputNode } from './NumberInputNode';
import { SelectNode } from './SelectNode';
import { SearchableSelectNode } from './SearchableSelectNode';
import { DatePickerNode } from './DatePickerNode';
import { CascaderNode } from './CascaderNode';
import { TextareaNode } from './TextareaNode';
import { MultiVariableNode } from './MultiVariableNode';
import { MultiVariableSelectNode } from './MultiVariableSelectNode';
import { UploadNode } from './UploadNode';
import { VideoUrlNode } from './VideoUrlNode';
import { VideoUploadNode } from './VideoUploadNode';
import { ReportUrlNode } from './ReportUrlNode';
import { ReportModuleNode } from './ReportModuleNode';
import { MarkdownNode } from './MarkdownNode';
import { MarkdownNameNode } from './MarkdownNameNode';
import { MarkdownModuleNode } from './MarkdownModuleNode';
import { RichtextNode } from './RichtextNode';
import { ShopDateSelectNode } from './ShopDateSelectNode';

export type NodeProps = {
  node: InputNode;
  value: string | string[] | undefined;
  onChange: (key: string, value: string | string[]) => void;
  onFileChange?: (key: string, file: File | string | null) => void;
  onAgentExit?: (reason?: 'tag-close' | 'skill-switch' | 'ai-image-switch') => void;
  hideAgentClose?: boolean;
  disabled?: boolean;
  error?: string;
  index?: number;
  files?: Record<string, File | string | null>;
  thumbnails?: Record<string, string>;
  onThumbnailChange?: (key: string, thumbnail: string) => void;
  videoUrlParse?: (params: { url: string }) => Promise<{ videoUrl: string; thumbnail: string }>;
  videoUpload?: (file: File, onProgress?: (percent: number) => void) => Promise<{ videoUrl: string; thumbnail: string }>;
  mediaUpload?: (
    file: File,
    onProgress?: (percent: number) => void,
  ) => Promise<{ mediaUrl: string; thumbnail?: string; mediaType?: 'audio' | 'video' | 'document' }>;
  shopListFetch?: ShopListFetcher;
  onVideoParsingChange?: (key: string, parsing: boolean) => void;
  // keyed 形态：父级传稳定的 useCallback，node.key 绑定在 NodeRenderer 内做（memo 隔离需要 props 引用稳定）
  onReportUrlParse?: (key: string, url: string) => Promise<void>;
  /** 报告列表数据源（dropdown 选择已有报告） */
  reportListFetch?: (params: {
    nodeKey: string;
    nodeType: 'report-url' | 'multi-variable' | 'multi-variable-select';
  }) => Promise<ReportListResult>;
  /** 空态时"去生成"跳转 */
  onReportGoGenerate?: (agentType: string | number) => void;
  /** 解析成功后的报告名（按 key 索引），传入则 chip 切到 name chip 模式 */
  reportNames?: Record<string, string>;
  /** name chip 清除按钮回调（外部应同时清空 value 与 reportName） */
  onClearReport?: (key: string) => void;
  /** multi-variable / multi-variable-select slot 失焦解析报告名（itemKey, url, nodeType）→ outcome */
  onResolveMultiName?: (
    itemKey: string,
    url: string,
    nodeType: 'multi-variable' | 'multi-variable-select',
  ) => Promise<ResolveNameOutcome>;
  /** multi-variable-select 选报告后触发模块解析（itemKey, url） */
  onAnalyzeMultiReport?: (itemKey: string, url: string) => void;
  /** multi-variable(-select) 清空槽位（splice 前移/尾部截断）时同步重排 reportNames（nodeKey, removedIdx） */
  onShiftReportNames?: (nodeKey: string, removedIdx: number) => void;
  /** dropdown item 的"查看报告"按钮：不传则 window.open(viewUrl||url) */
  onViewReport?: (item: ReportListItem) => void;
  /** dropdown 是否隐藏当前会话的报告 tab */
  hideCurrentSessionReport?: boolean;
  onMarkdownParse?: (key: string, text: string) => Promise<void>;
  onMarkdownNameParse?: (key: string, text: string) => Promise<void>;
  nodeDefaults?: {
    textarea?: { maxHeight?: number };
    richtext?: { minHeight?: number; maxHeight?: number };
  };
  /** richtext 展开/收起上报（key, expanded）。父级据此进入全屏态：隐藏其他节点 + 编辑区加全屏类。 */
  onRichtextExpandChange?: (key: string, expanded: boolean) => void;
};

// React.memo 节点隔离：在某节点输入/变更时，StructuredEditor 会重渲染并重跑 effectiveConfig.map，
// 给每个 NodeRenderer 生成新 element。默认浅比较下，未变动节点的 props 全部引用稳定
// （node 来自 useMemo 的 effectiveConfig；onChange/onFileChange 等是 useCallback；files/thumbnails/
// reportNames 在纯输入场景引用不变；value/error 仅变动节点不同），故兄弟节点跳过重渲染。
// 解析 callback（onReportUrlParse/onMarkdownParse/onMarkdownNameParse）已改为 keyed 形态
// （父级传稳定 useCallback，node.key 绑定下沉到本组件 switch 内），故 report-url/markdown(-name)
// 节点同样被 memo 隔离，不再因父级 per-render 闭包而漏隔离。
const NodeRendererImpl: React.FC<NodeProps> = (props) => {
  const { node } = props;
  switch (node.type) {
    case 'text':
      return <TextNode node={node} />;
    case 'tag': {
      // 智能体 tag：可退出（有 onAgentExit 且未 hideClose）时默认 hover 显示「点击退出智能体」气泡，
      // 与技能 tag（index.tsx 注入「点击退出技能」）一致。配置已显式带 tooltip 则尊重配置不覆盖。
      const canExit = !props.hideAgentClose && !!props.onAgentExit;
      const tagNode =
        canExit && !node.tooltip ? { ...node, tooltip: '点击退出智能体' } : node;
      return (
        <TagNode
          node={tagNode}
          hideClose={props.hideAgentClose}
          onClose={() => props.onAgentExit?.('tag-close')}
          // 与 SkillTag 一致：抬到技能面板（zIndex 9999）之上，否则面板打开时气泡被压住
          tooltipZIndex={10000}
        />
      );
    }
    case 'variable':
    case 'paste-text':
      return (
        <VariableNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'number-input':
      return (
        <NumberInputNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'select':
      return (
        <SelectNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'searchable-select':
      return (
        <SearchableSelectNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'datepicker':
      return (
        <DatePickerNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'shop-date-select':
      return (
        <ShopDateSelectNode
          node={node}
          value={Array.isArray(props.value) ? props.value : undefined}
          onChange={(key, nextValue) => props.onChange(key, nextValue)}
          shopListFetch={props.shopListFetch}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'cascader':
      return (
        <CascaderNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'textarea':
      return (
        <TextareaNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
          nodeDefaults={props.nodeDefaults}
        />
      );
    case 'multi-variable':
      return (
        <MultiVariableNode
          node={node}
          value={Array.isArray(props.value) ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
          reportListFetch={props.reportListFetch}
          onReportGoGenerate={props.onReportGoGenerate}
          onViewReport={props.onViewReport}
          hideCurrentSessionReport={props.hideCurrentSessionReport}
          reportNames={props.reportNames}
          onResolveName={props.onResolveMultiName}
          onClearReport={props.onClearReport}
          onShiftReportNames={props.onShiftReportNames}
        />
      );
    case 'multi-variable-select':
      return (
        <MultiVariableSelectNode
          node={node}
          value={Array.isArray(props.value) ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
          reportListFetch={props.reportListFetch}
          onReportGoGenerate={props.onReportGoGenerate}
          onViewReport={props.onViewReport}
          hideCurrentSessionReport={props.hideCurrentSessionReport}
          reportNames={props.reportNames}
          onResolveName={props.onResolveMultiName}
          onClearReport={props.onClearReport}
          onAnalyzeReport={props.onAnalyzeMultiReport}
          onShiftReportNames={props.onShiftReportNames}
        />
      );
    case 'upload':
      return (
        <UploadNode
          node={node}
          files={props.files ?? {}}
          onFileChange={props.onFileChange ?? (() => {})}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'video-url':
      return (
        <VideoUrlNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          thumbnail={node.key ? props.thumbnails?.[node.key] : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          onThumbnailChange={props.onThumbnailChange}
          videoUrlParse={props.videoUrlParse}
          onParsingChange={props.onVideoParsingChange}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'video-upload':
    case 'media-upload':
      return (
        <VideoUploadNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          thumbnail={node.key ? props.thumbnails?.[node.key] : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          onThumbnailChange={props.onThumbnailChange}
          onFileChange={props.onFileChange}
          videoUpload={props.videoUpload}
          mediaUpload={props.mediaUpload}
          onParsingChange={props.onVideoParsingChange}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'report-url':
      return (
        <ReportUrlNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          onParse={
            node.key && props.onReportUrlParse
              ? (url) => props.onReportUrlParse!(node.key!, url)
              : undefined
          }
          reportListFetch={props.reportListFetch}
          onReportGoGenerate={props.onReportGoGenerate}
          reportName={
            node.key && props.reportNames ? props.reportNames[node.key] : undefined
          }
          onClearReport={
            props.onClearReport && node.key
              ? () => props.onClearReport!(node.key!)
              : undefined
          }
          onViewReport={props.onViewReport}
          hideCurrentSessionReport={props.hideCurrentSessionReport}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'markdown':
      return (
        <MarkdownNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          onParse={
            node.key && props.onMarkdownParse
              ? (text) => props.onMarkdownParse!(node.key!, text)
              : undefined
          }
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'markdown-name':
      return (
        <MarkdownNameNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          onParse={
            node.key && props.onMarkdownNameParse
              ? (text) => props.onMarkdownNameParse!(node.key!, text)
              : undefined
          }
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'report-module':
      return (
        <ReportModuleNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'markdown-module':
      return (
        <MarkdownModuleNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
        />
      );
    case 'richtext':
      return (
        <RichtextNode
          node={node}
          value={typeof props.value === 'string' ? props.value : undefined}
          onChange={(k, v) => props.onChange(k, v)}
          disabled={props.disabled}
          error={props.error}
          nodeDefaults={props.nodeDefaults}
          onExpandChange={props.onRichtextExpandChange}
        />
      );
    default:
      return (
        <DebugNode
          node={props.node}
          value={props.value}
          onChange={props.onChange}
          index={props.index}
        />
      );
  }
};

export const NodeRenderer = React.memo(NodeRendererImpl);
NodeRenderer.displayName = 'NodeRenderer';
